// useSpeechRecognition.ts
import { useEffect, useRef } from 'react'
import { SpeechRecognition, SpeechRecognitionEvent } from '@/@types/global'

interface UseSpeechRecognitionOptions {
  lang: string
  baseText: string
  onResult: (text: string) => void
  onError?: (error: string) => void
}

export const useSpeechRecognition = ({ lang, baseText, onResult, onError }: UseSpeechRecognitionOptions) => {
  const recognitionRef = useRef<{
    recognition: SpeechRecognition | null
    isListening: boolean
  }>({
    recognition: null,
    isListening: false
  })

  const baseTextRef = useRef(baseText)

  useEffect(() => {
    baseTextRef.current = baseText
  }, [baseText])

  useEffect(() => {
    const unsafeWindow = window as any
    const SpeechRecognition = unsafeWindow.SpeechRecognition || unsafeWindow.webkitSpeechRecognition

    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser')
      return
    }

    const recognition = new SpeechRecognition()
    recognitionRef.current.recognition = recognition

    recognition.continuous = true
    recognition.lang = `${lang.toLowerCase()}-${lang.toUpperCase()}`

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = ''
      let interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }

      const combinedText = (baseTextRef.current + ' ' + finalTranscript).trim()
      onResult((combinedText + ' ' + interimTranscript).trim())
    }

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      onError?.(event.error)
    }

    recognition.onend = () => {
      if (recognitionRef.current.isListening) {
        recognition.start()
      }
    }

    return () => {
      recognitionRef.current.isListening = false
      recognition.stop()
    }
  }, [lang])

  const start = () => {
    recognitionRef.current.isListening = true
    recognitionRef.current.recognition?.start()
  }

  const stop = () => {
    recognitionRef.current.isListening = false
    recognitionRef.current.recognition?.stop()
  }

  return {
    start,
    stop,
    isListening: () => recognitionRef.current.isListening
  }
}
